// src/components/core/DocumentValidationTag.jsx
// *******************************************************************************
// DOCUMENT VALIDATION TAG
// *******************************************************************************
// Displays validation status icon (green check or yellow warning).
// Shows tooltip with validation status description.
// On click (when invalid), opens modal with detailed warnings.
// Returns null if documentValidation doesn't exist.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState } from 'react';
import { Modal, Typography, Tooltip, Space, Collapse } from 'antd';
import {
  CheckCircleFilled,
  WarningFilled,
  CloseCircleFilled,
  CheckCircleOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DocumentValidationTag = ({ data, showTitle = false, style = {} }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const documentValidation = data?.documentValidation;
  const isValid = documentValidation?.isValid;
  const errors = documentValidation?.errorList || [];
  const warnings = documentValidation?.warningList || [];
  const hasIssues = errors.length > 0 || warnings.length > 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClick = e => {
    e.stopPropagation();
    e.preventDefault();
    // Open modal for failed validations (regardless of warnings count)
    if (!isValid) {
      setModalOpen(true);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Return null if documentValidation doesn't exist
  if (!documentValidation) return null;

  const tooltipText = isValid
    ? translateX('validation_success_tooltip')
    : translateX('validation_failed_tooltip');

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  const iconStyle = {
    fontSize: 16,
    cursor: !isValid ? 'pointer' : 'default',
    ...style,
  };

  return (
    <>
      <Tooltip title={tooltipText} placement="top">
        <span
          onClick={!isValid ? handleClick : undefined}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {isValid ? (
            <CheckCircleOutlined style={{ color: '#52c41a', ...iconStyle }} />
          ) : (
            <WarningFilled style={{ color: '#faad14', ...iconStyle }} />
          )}
          {showTitle && (
            <Text style={{ fontSize: 11 }}>
              {isValid ? translateX('validated') : translateX('validation_failed')}
            </Text>
          )}
        </span>
      </Tooltip>

      {/* Modal with validation errors and warnings */}
      <Modal
        title={translateX('validation_issues')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={600}
      >
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          {/* Errors section */}
          {errors.length > 0 && (
            <div>
              <Text strong style={{ color: '#ff4d4f', display: 'block', marginBottom: 8 }}>
                {translateX('errors')} ({errors.length})
              </Text>
              <div>
                {errors.map((error, idx) => {
                  const code = typeof error === 'object' ? error.code : null;
                  const message = typeof error === 'object' ? error.message : error;
                  const field = typeof error === 'object' ? error.field : null;
                  const translatedMessage = code;
                  const hasTechnicalDetails = message || field;

                  return (
                    <div
                      key={idx}
                      style={{
                        padding: '8px 0',
                        // borderBottom: '1px solid #f0f0f0'
                      }}
                    >
                      <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <CloseCircleFilled
                            style={{ color: '#ff4d4f', marginRight: 8, marginTop: 4 }}
                          />
                          <Text>{translatedMessage || message || error}</Text>
                        </div>
                        {hasTechnicalDetails && translatedMessage && (
                          <Collapse
                            size="small"
                            style={{ marginTop: 8, marginLeft: 22 }}
                            items={[
                              {
                                key: '1',
                                label: translateX('description'),
                                children: (
                                  <div>
                                    {message && (
                                      <Text
                                        type="secondary"
                                        style={{ display: 'block', fontSize: 12 }}
                                      >
                                        {message}
                                      </Text>
                                    )}
                                    {field && (
                                      <Text
                                        type="secondary"
                                        style={{ display: 'block', fontSize: 12, marginTop: 4 }}
                                      >
                                        {translateX('field')}: {field}
                                      </Text>
                                    )}
                                  </div>
                                ),
                              },
                            ]}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Warnings section */}
          {warnings.length > 0 && (
            <div>
              <Text strong style={{ color: '#faad14', display: 'block', marginBottom: 8 }}>
                {translateX('warnings')} ({warnings.length})
              </Text>
              <div>
                {warnings.map((warning, idx) => {
                  const code = typeof warning === 'object' ? warning.code : null;
                  const message = typeof warning === 'object' ? warning.message : warning;
                  const field = typeof warning === 'object' ? warning.field : null;
                  const translatedMessage = code ? translateX(code) : null;
                  const hasTechnicalDetails = message || field;

                  return (
                    <div
                      key={idx}
                      style={{
                        padding: '8px 0',
                        // borderBottom: '1px solid #f0f0f0'
                      }}
                    >
                      <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <WarningFilled
                            style={{ color: '#faad14', marginRight: 8, marginTop: 4 }}
                          />
                          <Text>{translatedMessage || message || warning}</Text>
                        </div>
                        {hasTechnicalDetails && translatedMessage && (
                          <Collapse
                            size="small"
                            style={{ marginTop: 8, marginLeft: 22 }}
                            items={[
                              {
                                key: '1',
                                label: translateX('description'),
                                children: (
                                  <div>
                                    {message && (
                                      <Text
                                        type="secondary"
                                        style={{ display: 'block', fontSize: 12 }}
                                      >
                                        {message}
                                      </Text>
                                    )}
                                    {field && (
                                      <Text
                                        type="secondary"
                                        style={{ display: 'block', fontSize: 12, marginTop: 4 }}
                                      >
                                        {translateX('field')}: {field}
                                      </Text>
                                    )}
                                  </div>
                                ),
                              },
                            ]}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No issues message */}
          {!hasIssues && <Text type="secondary">{translateX('no_validation_issues')}</Text>}
        </Space>
      </Modal>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DocumentValidationTag;

// *******************************************************************************
// HELPERS
// *******************************************************************************
